import { Link, useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { PlusIcon, SearchIcon } from "../../components/icons";
import { Avatar, Empty, ListSkeleton, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatPhone, shortTime } from "../../lib/format";
import { type QuoteFilter, useCreateQuote, useQuotes, useTeam } from "../../lib/queries";
import { ContactPicker } from "../cases/CaseDialog";
import { money, quoteNumber, STATUS_LABEL, STATUS_PILL, shownStatus } from "./quote-text";

const VIEWS: { key: string; label: string; filter: QuoteFilter }[] = [
  { key: "open", label: "Abiertas", filter: { state: "open" } },
  { key: "mine", label: "Mías", filter: { state: "open", assignee: "me" } },
  { key: "draft", label: "Borradores", filter: { state: "draft" } },
  { key: "sent", label: "Enviadas", filter: { state: "sent" } },
  { key: "expired", label: "Vencidas", filter: { state: "expired" } },
  { key: "accepted", label: "Aceptadas", filter: { state: "accepted" } },
  { key: "all", label: "Todas", filter: { state: "all" } },
];

/** Quotes: drafts, sent (and expired), accepted and rejected, newest touched first. */
export function QuotesPage() {
  const [view, setView] = useState("open");
  const [q, setQ] = useState("");
  const deferred = useDeferredValue(q.trim());
  const list = useQuotes({ ...VIEWS.find((v) => v.key === view)?.filter, q: deferred });
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const [creating, setCreating] = useState(false);
  const items = list.data ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Cotizaciones</h1>
      </div>
      <div className="page-head">
        <label className="search-field">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Buscar COT-0042, cliente o referencia"
            aria-label="Buscar cotizaciones"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <fieldset className="filters">
          <legend className="sr-only">Mostrar</legend>
          {VIEWS.map((v) => (
            <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => setView(v.key)}>
              {v.label}
            </button>
          ))}
        </fieldset>
        <div className="products-actions">
          <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>
            <PlusIcon />
            Nueva cotización
          </button>
        </div>
      </div>

      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={deferred ? "Ninguna cotización coincide." : "No hay cotizaciones aquí."}>
            <span>
              {deferred
                ? "Prueba con otro número, nombre o referencia."
                : "Se crean desde la conversación del cliente, con el botón Cotizar, o aquí con Nueva cotización."}
            </span>
          </Empty>
        ) : (
          <ul className="entity-list">
            {items.map((x) => {
              const status = shownStatus(x);
              return (
                <li key={x.id}>
                  <Link
                    to="/cotizaciones/$quoteId"
                    params={{ quoteId: x.id }}
                    className="entity-row case-row quote-row"
                  >
                    <span className="case-number mono">{quoteNumber(x.number)}</span>
                    <span className="entity-main">
                      <span className="entity-name">{x.contactName ?? formatPhone(x.contactWaId)}</span>
                      <span className="entity-meta">
                        <span>
                          {x.firstLine ?? "Sin líneas"}
                          {x.lineCount > 1 ? ` y ${x.lineCount - 1} más` : ""}
                        </span>
                        {x.reference && <span className="mono">{x.reference}</span>}
                      </span>
                    </span>
                    <span className="entity-flags">
                      <span className="quote-total mono">{money(x.total, x.currency)}</span>
                      <span className={`pill ${STATUS_PILL[status]}`}>{STATUS_LABEL[status]}</span>
                      {x.assigneeId && (
                        <span className="case-assignee" title={names.get(x.assigneeId)}>
                          <Avatar name={names.get(x.assigneeId) ?? null} fallback="?" />
                        </span>
                      )}
                      <span className="faint entity-time">{shortTime(x.updatedAt)}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {creating && <NewQuoteDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** Picks the customer and opens the new draft. */
function NewQuoteDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const create = useCreateQuote();
  const navigate = useNavigate();
  const toast = useToast();
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function pick(contact: { id: string }) {
    try {
      const q = await create.mutateAsync({ contactId: contact.id });
      dialog.current?.close();
      navigate({ to: "/cotizaciones/$quoteId", params: { quoteId: q.id } });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo crear la cotización.");
    }
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="quote-new-title" className="case-dialog">
      <div className="dialog-form">
        <h3 id="quote-new-title">Nueva cotización</h3>
        <ContactPicker onPick={pick} idPrefix="quote-new" />
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
        </div>
      </div>
    </dialog>
  );
}
